import type { Person } from '@local/common-lib';
import { ArrowBack as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDateTimePicker,
} from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon/index.js';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Collapse,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { FormikCheckbox } from '../../components/formik-fields/check-box.js';
import { FormikNumberField } from '../../components/formik-fields/number-field.js';
import { FormikTextField } from '../../components/formik-fields/text-field.js';
import { CenterCard } from '../../components/page-layout/index.js';
import type { EditViewProps } from '../../utils/types.js';
import { CallSignField } from './call-sign-field.js';
import type { CallSignOptions } from './use-call-signs.js';

export interface QuickPatrolData {
  callSign: string;
  hours: number;
  name: string;
  patrollers: string[];
  sendToEccLogOn: boolean;
  startIn10Min: boolean;
  when: Date;
}

interface QuickPatrolViewProps
  extends EditViewProps<QuickPatrolData>,
    CallSignOptions {
  persons: Person.View[];
  personsLoading: boolean;
}

export const QuickPatrolView: FC<QuickPatrolViewProps> = ({
  callSigns,
  cancel,
  formik,
  persons,
  personsLoading,
  restrictCallSigns,
}) => {
  const minDate = DateTime.now().plus({ minutes: 5 });
  const maxDate = DateTime.now().plus({ hours: 12 });

  return (
    <form onReset={formik.handleReset} onSubmit={formik.handleSubmit}>
      <CenterCard>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Stack direction="column" spacing={3} sx={{ p: 3 }}>
            <Typography variant="h5">Quick Patrol</Typography>
            <Typography color="text.secondary" variant="body2">
              Create a patrol for the near future (5 minutes to 12 hours from
              now) with you as the assigned patroller.
            </Typography>

            <Alert severity="info">
              ECC processing can take up to 30 minutes. If starting soon, you
              may need to phone ECC directly.
            </Alert>

            <FormikTextField
              formik={formik}
              fullWidth
              id="name"
              label="Patrol name"
            />

            <CallSignField
              callSigns={callSigns}
              formik={formik}
              restrictCallSigns={restrictCallSigns}
            />

            <FormikNumberField
              formik={formik}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">hours</InputAdornment>
                ),
              }}
              id="hours"
              inputProps={{ step: 'any' }}
              label="Patrol duration"
              type="number"
            />

            <Autocomplete<Person.View, true>
              autoComplete
              autoHighlight
              clearOnEscape={true}
              fullWidth
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName?.toUpperCase()}`
              }
              id="patrollers"
              loading={personsLoading}
              multiple
              noOptionsText="No-one found"
              onChange={(_, selected) =>
                formik.setFieldValue(
                  'patrollers',
                  selected.map((p) => p._id),
                )
              }
              options={persons}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(formik.errors.patrollers)}
                  helperText={
                    formik.errors.patrollers
                      ? String(formik.errors.patrollers)
                      : 'You will be automatically assigned as the primary patroller'
                  }
                  label="Additional Patrollers"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.firstName} ${option.lastName?.toUpperCase()}`}
                    {...getTagProps({ index })}
                    key={option._id}
                    size="small"
                  />
                ))
              }
              value={persons.filter((p) =>
                formik.values.patrollers.includes(p._id),
              )}
            />

            <FormikCheckbox
              formik={formik}
              helperText="Automatically set start time to 10 minutes from now when you create the patrol"
              id="startIn10Min"
              label="Start ASAP"
            />

            <Box>
              <Collapse in={formik.values.startIn10Min}>
                <Alert severity="info">
                  Patrol will start at{' '}
                  {DateTime.now()
                    .plus({ minutes: 10 })
                    .toLocaleString(DateTime.DATETIME_SHORT)}
                </Alert>
              </Collapse>

              <Collapse in={!formik.values.startIn10Min}>
                <Stack direction="column" spacing={3}>
                  <MobileDateTimePicker
                    disabled={formik.isSubmitting || formik.values.startIn10Min}
                    label="Start time"
                    maxDateTime={maxDate}
                    minDateTime={minDate}
                    onChange={(newValue: DateTime | null) => {
                      if (newValue) {
                        formik.setFieldValue('when', newValue.toJSDate());
                      }
                    }}
                    renderInput={(params: object) => (
                      <TextField {...params} fullWidth />
                    )}
                    value={DateTime.fromJSDate(formik.values.when)}
                  />

                  <FormikCheckbox
                    formik={formik}
                    helperText="Navigate to ECC Login form immediately after creating the patrol"
                    id="sendToEccLogOn"
                    label="Go to ECC Login after creating"
                  />
                </Stack>
              </Collapse>
            </Box>

            <Stack direction="row" spacing={2} sx={{ justifyContent: 'end' }}>
              <LoadingButton
                color="primary"
                loading={formik.isSubmitting}
                onClick={cancel}
                startIcon={<CancelIcon />}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                color="primary"
                loading={formik.isSubmitting}
                startIcon={<SaveIcon />}
                type="submit"
                variant="contained"
              >
                Create Patrol
              </LoadingButton>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </CenterCard>
    </form>
  );
};
